import React from "react";
import { Box, makeStyles } from "@material-ui/core";
import { LockOutlined, ArrowForward } from "@material-ui/icons";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";
import { SKCard, SKTypography, SKButton } from "../../ui";

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(3),
    width: "100%",
  },
  lockedCard: {
    background: "linear-gradient(135deg, #ffffff 0%, #ebf8ff 100%)",
    border: "1px solid #bee3f8",
    borderRadius: 16,
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
    overflow: "hidden",
    position: "relative",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)",
      transform: "translateY(-2px)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: 6,
      height: "100%",
      backgroundColor: theme.palette.primary.main,
    },
  },
  content: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2.5, 3),
      gap: theme.spacing(2),
    },
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
    },
  },
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 56,
    height: 56,
    borderRadius: "50%",
    backgroundColor: "#ebf8ff",
    border: "2px solid #bee3f8",
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  textContainer: {
    flexGrow: 1,
  },
  title: {
    fontWeight: 800,
    color: "#2d3748",
    marginBottom: theme.spacing(0.5),
    fontSize: "1.1rem",
  },
  description: {
    color: "#718096",
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },
  actionWrapper: {
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      marginTop: theme.spacing(1),
      width: "100%",
    },
  },
  upgradeButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: theme.spacing(1, 3),
    fontWeight: 700,
    borderRadius: 8,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
}));

const FeatureLockedAlert = ({ title, description, buttonLabel, onAction, className = "" }) => {
  const classes = useStyles();
  const router = useRouter();
  const { t } = useTranslation("common");

  const handleAction = () => {
    if (onAction) {
      onAction();
    } else {
      router.push("/subscription");
    }
  };

  return (
    <Box className={`${classes.root} ${className}`}>
      <SKCard className={classes.lockedCard} variant="static">
        <Box className={classes.content}>
          <Box className={classes.iconWrapper}>
            <LockOutlined fontSize="large" />
          </Box>
          <Box className={classes.textContainer}>
            <SKTypography variant="h6" className={classes.title}>
              {title || t("Feature Locked")}
            </SKTypography>
            <SKTypography variant="body2" className={classes.description}>
              {description ||
                t(
                  "This feature is not available in your current plan. Please upgrade your subscription to unlock this and other advanced features.",
                )}
            </SKTypography>
          </Box>
          <Box className={classes.actionWrapper}>
            <SKButton
              variant="contained"
              onClick={handleAction}
              endIcon={<ArrowForward />}
              className={classes.upgradeButton}
            >
              {buttonLabel || t("Upgrade Plan")}
            </SKButton>
          </Box>
        </Box>
      </SKCard>
    </Box>
  );
};

export default FeatureLockedAlert;
